// utils/elementFormat/location.js
//
// How a `location` element PARSES and RENDERS (docs/plans/data-types.md,
// decision 5 and the frozen wire contract): one element that holds an
// address, a pair of coordinates, or both —
//
//     { address?: string, lat?: number, lng?: number }
//
// with ONLY the keys that are actually set. A stored `{ lat: 0, lng: 0 }` is
// the Gulf of Guinea and a stored `{}` is nothing at all; the shape has to be
// able to say which, so absent keys are absent rather than null or ''.
//
// PURE AND DEPENDENCY-FREE ON PURPOSE, and vendored byte-identical into
// keepr-web at `src/utils/elementFormat/location.js` so the form's inline
// feedback and the API's 400 `invalid_location` agree exactly. MIRRORED in
// keepr-web — change them together. Nothing here may require() anything, and
// nothing here may know about Mongo, Boom or the request.
//
// Deliberately NOT here: geocoding (turning an address into coordinates or
// back) and map rendering, both out of scope per decision 12. This module
// stores what the user gave it and links out to a map that knows more.
//
// The one piece of cleverness, and it is the user's own request: a STRING
// that spells a coordinate pair ("37.7749, -122.4194") is read as
// coordinates, not as an address. A person who has coordinates on the
// clipboard pastes them into the address box, and a platform that then files
// them as a street name has misunderstood something obvious. The cost is that
// a literal address of two bare numbers ("12, 34") is read as a latitude and
// a longitude — a trade the plan takes knowingly, because such an address
// does not exist and such a coordinate pair does.

'use strict';

const LAT_LIMIT = 90;
const LNG_LIMIT = 180;

// Coordinates render to 5 decimal places: roughly a metre at the equator,
// which is finer than any consumer GPS fix and short enough to read.
const DISPLAY_DECIMALS = 5;

// "37.7749, -122.4194" / "12,34" / "+37.7749 , -122.4194". Two plain decimal
// numbers around one comma, and nothing else — an exponent or a stray letter
// makes it an address again.
const COORD_PAIR_RE = /^([+-]?\d+(?:\.\d+)?)\s*,\s*([+-]?\d+(?:\.\d+)?)$/;

const isFiniteNumber = v => typeof v === 'number' && Number.isFinite(v);
const isPlainObject = v => v !== null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date);

/** A number, or the number a plain numeric string spells; null otherwise. */
function numberOf(raw) {
	if (isFiniteNumber(raw)) return raw;
	if (typeof raw === 'string') {
		const text = raw.trim();
		if (text && /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) {
			const n = Number(text);
			return Number.isFinite(n) ? n : null;
		}
	}
	return null;
}

/**
 * A value with NOTHING IN IT: null, '', a blank string, `{}`, or an object
 * whose address is blank and whose coordinates were not given at all.
 *
 * "Not given at all" and not "not a usable pair", deliberately: `{ lat: 51.5 }`
 * is half a coordinate, which is a MISTAKE the write path must report
 * (`coords_pair`), not an empty field to skip past. Emptiness means the user
 * left it alone; a lone latitude means they did not.
 */
function isEmptyLocation(value) {
	if (value === null || value === undefined || value === '') return true;
	if (typeof value === 'string') return value.trim() === '';
	if (!isPlainObject(value)) return false;
	const blank = v => v === undefined || v === null || v === '';
	const hasAddress = typeof value.address === 'string' ? value.address.trim() !== '' : !blank(value.address);
	return !hasAddress && blank(value.lat) && blank(value.lng);
}

/**
 * Which halves a STORED value carries: 'address', 'coordinates', 'both', or
 * null when it carries neither.
 *
 * This is what the element's `accept` option ('both' | 'address' |
 * 'coordinates') is checked against on write, and what the web's input uses
 * to decide which half to reveal. It lives here, next to the shape it reads,
 * so the two repos cannot drift on what "an address value" means.
 */
function locationKind(value) {
	if (!isPlainObject(value)) return null;
	const hasAddress = typeof value.address === 'string' && value.address.trim() !== '';
	const hasCoords = isFiniteNumber(value.lat) && isFiniteNumber(value.lng);
	if (hasAddress && hasCoords) return 'both';
	if (hasAddress) return 'address';
	if (hasCoords) return 'coordinates';
	return null;
}

/**
 * Is this the stored shape of a location? True for a plain object whose keys
 * are drawn from { address, lat, lng } and that carries at least one of them.
 *
 * The platform asks this where it has a VALUE but no element definition —
 * most importantly the change log, which treats a location as ONE value so a
 * move records "Town Hall -> Old Mill" rather than three sub-path rows the
 * revert could not reassemble.
 */
function isLocationValue(value) {
	if (!isPlainObject(value)) return false;
	const keys = Object.keys(value);
	if (!keys.length) return false;
	return keys.every(k => k === 'address' || k === 'lat' || k === 'lng');
}

/** { value: <shape>, valid: true } with only the keys that are set. */
function built(address, lat, lng) {
	const value = {};
	if (address) value.address = address;
	if (lat !== null) { value.lat = lat; value.lng = lng; }
	return { value, valid: true };
}

const invalid = reason => ({ value: null, valid: false, reason });

/**
 * The lat/lng half of an object input: `{ lat, lng }` when both are present
 * and in range, `{ lat: null }` when neither is, an `invalid` otherwise.
 * Coordinates come as a PAIR — half a coordinate locates nothing, and
 * silently dropping the half that was given would lose data the user typed.
 */
function coordsOf(rawLat, rawLng) {
	const latGiven = rawLat !== undefined && rawLat !== null && rawLat !== '';
	const lngGiven = rawLng !== undefined && rawLng !== null && rawLng !== '';
	if (!latGiven && !lngGiven) return { lat: null, lng: null };
	if (!latGiven || !lngGiven) return invalid('coords_pair');

	const lat = numberOf(rawLat);
	const lng = numberOf(rawLng);
	if (lat === null || lng === null) return invalid('type');
	if (lat < -LAT_LIMIT || lat > LAT_LIMIT) return invalid('lat_range');
	if (lng < -LNG_LIMIT || lng > LNG_LIMIT) return invalid('lng_range');
	return { lat, lng };
}

/**
 * parseLocation(raw) -> { value, valid, reason? }
 *
 *   value   the shape to STORE — `{ address?, lat?, lng? }` with only the
 *           keys that are set — or null when the input is empty or invalid.
 *   valid   false only when the input was MEANT as a location and is wrong.
 *           An EMPTY input is `{ value: null, valid: true }`: a blank
 *           location element is blank, not broken, and telling those two
 *           apart is the caller's job (`value === null` is "nothing here").
 *   reason  a short machine token when invalid, so the API and the web can
 *           hang the same message on it: 'type' (not a location shape at
 *           all), 'coords_pair' (one coordinate without the other),
 *           'lat_range' / 'lng_range' (outside ±90 / ±180).
 *
 * Accepts the stored object, or a STRING — which is a coordinate pair when it
 * spells one and an address otherwise.
 */
function parseLocation(raw) {
	if (raw === undefined || raw === null) return { value: null, valid: true };

	if (typeof raw === 'string') {
		const text = raw.trim();
		if (!text) return { value: null, valid: true };
		const pair = COORD_PAIR_RE.exec(text);
		if (!pair) return built(text, null, null);
		// A pair of bare numbers is coordinates — including when they are out
		// of range, which is a typo'd coordinate and deserves to be TOLD so,
		// rather than quietly filed as a street address nobody can find.
		const coords = coordsOf(pair[1], pair[2]);
		if (coords.valid === false) return coords;
		return built('', coords.lat, coords.lng);
	}

	if (!isPlainObject(raw)) return invalid('type');

	// An address key that is present but not a string is a shape error, not
	// an empty address: `{ address: 42 }` means the caller is confused.
	let address = '';
	if (raw.address !== undefined && raw.address !== null) {
		if (typeof raw.address !== 'string') return invalid('type');
		address = raw.address.trim();
	}
	const coords = coordsOf(raw.lat, raw.lng);
	if (coords.valid === false) return coords;
	if (!address && coords.lat === null) return { value: null, valid: true };
	return built(address, coords.lat, coords.lng);
}

/** A coordinate trimmed to DISPLAY_DECIMALS with no trailing-zero padding. */
function coordText(n) {
	return String(Number(n.toFixed(DISPLAY_DECIMALS)));
}

/**
 * formatLocation(value) -> string
 *
 * The address when there is one, else the coordinates as "37.7749,
 * -122.4194"; '' when there is nothing. The address wins because it is what
 * the user WROTE — coordinates are how a machine finds the place, an address
 * is how a person recognises it.
 *
 * A bare string is formatted as the location it parses to, so a legacy value
 * from before the type existed still reads.
 */
function formatLocation(value) {
	if (typeof value === 'string') return value.trim();
	if (!isPlainObject(value)) return '';
	const address = typeof value.address === 'string' ? value.address.trim() : '';
	if (address) return address;
	if (isFiniteNumber(value.lat) && isFiniteNumber(value.lng)) {
		return `${coordText(value.lat)}, ${coordText(value.lng)}`;
	}
	return '';
}

/**
 * mapsUrl(value) -> 'https://www.google.com/maps/search/?api=1&query=…' | ''
 *
 * The href behind the Open in Maps action (decision 8, "values carry their
 * actions"). COORDINATES WIN when the value has both: an address is a
 * request for the map to guess, and coordinates are the answer it would have
 * guessed at — no ambiguity, no wrong "Main Street" in the wrong city.
 *
 * `api=1` is Google's documented, key-free URL contract, so the link keeps
 * working without the platform holding a Maps credential.
 */
function mapsUrl(value) {
	const parsed = typeof value === 'string' || isPlainObject(value) ? parseLocation(value) : { value: null, valid: true };
	const v = parsed.valid ? parsed.value : null;
	if (!v) return '';
	const query = isFiniteNumber(v.lat) && isFiniteNumber(v.lng)
		? `${v.lat},${v.lng}`
		: encodeURIComponent(v.address || '');
	if (!query) return '';
	return `https://www.google.com/maps/search/?api=1&query=${query}`;
}

module.exports = {
	LAT_LIMIT,
	LNG_LIMIT,
	DISPLAY_DECIMALS,
	parseLocation,
	formatLocation,
	mapsUrl,
	isEmptyLocation,
	isLocationValue,
	locationKind,
};
